import {
  ArrowDownLeft,
  ArrowUpRight,
  Ban,
  CheckCircle2,
  Copy,
  History,
  MessageSquare,
  MessageSquareOff,
  Phone,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactPill } from "@/components/marketing/contacts/ContactPill";
import { ExcludedPill, NO_NAME } from "@/components/marketing/contacts/ContactsTable";
import { useContactHistory } from "@/hooks/sms/useContactHistory";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneNumber } from "@/utils/phoneFormatter";
import {
  CONSENT_METHOD_LABELS,
  describeInvites,
  describeSource,
  describeTextClub,
  effectiveInviteCount,
  formatContactDate,
  lastInvitedAt,
  type TimelineKind,
} from "@/utils/contacts";
import type { ContactListRow } from "@/types/sms";

interface ContactDetailSheetProps {
  contact: ContactListRow | null;
  onOpenChange: (open: boolean) => void;
  timezone: string;
  ebrWindowDays: number;
  /** Opens the marketing exclusion confirmation; omitted while exclusions are unavailable. */
  onToggleMarketing?: (contact: ContactListRow) => void;
}

const TIMELINE_VISUALS: Record<TimelineKind, { Icon: LucideIcon; chip: string }> = {
  opt_in: { Icon: CheckCircle2, chip: "bg-status-success/10 text-status-success" },
  opt_out: { Icon: Ban, chip: "bg-status-danger/10 text-status-danger" },
  consent: { Icon: History, chip: "bg-muted text-muted-foreground" },
  outbound: { Icon: ArrowUpRight, chip: "bg-muted text-muted-foreground" },
  inbound: { Icon: ArrowDownLeft, chip: "bg-status-info/10 text-status-info" },
  failed: { Icon: TriangleAlert, chip: "bg-status-danger/10 text-status-danger" },
};

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

/**
 * "View details and history" for one contact: status with the consent event
 * behind it, the facts the roster row carries, and every consent change and
 * text in or out, newest first. Call, Copy, and "Don't send marketing texts"
 * (or its undo, behind a confirmation) are the only actions.
 */
export function ContactDetailSheet({ contact, onOpenChange, timezone, ebrWindowDays, onToggleMarketing }: ContactDetailSheetProps) {
  const { toast } = useToast();
  const history = useContactHistory(contact?.customer_phone ?? null);

  const date = (iso: string | null) => formatContactDate(iso, timezone);

  return (
    <Sheet open={!!contact} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md">
        {contact && (() => {
          const named = !!contact.display_name?.trim();
          const formatted = formatPhoneNumber(contact.customer_phone);
          const club = describeTextClub(contact, timezone);
          const invites = describeInvites(contact, ebrWindowDays, timezone);
          const latest = history.data?.latestConsent;
          const how = latest ? (CONSENT_METHOD_LABELS[latest.method] ?? latest.method.replace(/_/g, " ")) : "";
          const why =
            latest && latest.to_status === contact.opt_in_status
              ? `${how.charAt(0).toUpperCase()}${how.slice(1)} on ${date(latest.created_at)}`
              : null;
          const inviteCount = effectiveInviteCount(contact);

          const copy = async () => {
            try {
              await navigator.clipboard.writeText(formatted);
              toast({ title: "Phone number copied" });
            } catch {
              toast({ title: "Couldn't copy the number", description: formatted, variant: "destructive" });
            }
          };

          return (
            <>
              <SheetHeader className="space-y-1 border-b border-border p-4 pr-12 text-left sm:p-6 sm:pr-12">
                <SheetTitle className={named ? "text-foreground" : "text-muted-foreground"}>
                  {named ? contact.display_name!.trim() : NO_NAME}
                </SheetTitle>
                <SheetDescription className="tabular-nums">{formatted}</SheetDescription>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button asChild variant="outline" className="h-11">
                    <a href={`tel:+1${contact.customer_phone}`}>
                      <Phone className="h-4 w-4 text-status-info" aria-hidden="true" />
                      Call
                    </a>
                  </Button>
                  <Button variant="outline" className="h-11" onClick={copy}>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Copy number
                  </Button>
                </div>
              </SheetHeader>

              <div className="space-y-5 p-4 sm:p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <ContactPill tone={club.tone} label={club.label} />
                  {contact.never_invite && <ContactPill tone="muted" label="Do not invite" icon={false} />}
                  <span className="text-sm text-muted-foreground">
                    {why ?? club.since ?? (contact.opt_in_status === "unknown" ? "Never asked to join the text club" : null)}
                  </span>
                  {contact.marketing_excluded && <ExcludedPill />}
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <Fact label="Source">{describeSource(contact.source, contact.source_ref)}</Fact>
                  <Fact label="Messaging">
                    {contact.messaging_status === "disabled"
                      ? `Disabled${contact.messaging_disabled_reason ? ` · ${contact.messaging_disabled_reason}` : ""}`
                      : "Enabled"}
                  </Fact>
                  <Fact label="Orders">
                    {contact.total_orders != null
                      ? `${contact.total_orders}${contact.first_order_at ? ` · first ${date(contact.first_order_at)}` : ""}`
                      : "None on record"}
                  </Fact>
                  <Fact label="Last order">{date(contact.last_order_at) ?? "None on record"}</Fact>
                  <Fact label="Invites">
                    {inviteCount > 0
                      ? `${inviteCount}${lastInvitedAt(contact) ? ` · last ${date(lastInvitedAt(contact))}` : ""}`
                      : invites.label === "—"
                        ? "None"
                        : `${invites.label}${invites.detail ? ` · ${invites.detail}` : ""}`}
                  </Fact>
                  <Fact label="Loyalty">{contact.is_loyalty ? "Member" : "No"}</Fact>
                  <Fact label="Email">{contact.customer_email || "None on file"}</Fact>
                  <Fact label="Customer since">
                    {history.isLoading ? "…" : (date(history.data?.customerSince ?? null) ?? "Unknown")}
                  </Fact>
                </dl>

                {onToggleMarketing && (
                  <section className="rounded-lg border border-border p-3">
                    <h3 className="text-sm font-medium text-foreground">Marketing texts</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {contact.marketing_excluded
                        ? `Off${contact.marketing_excluded_at ? ` since ${date(contact.marketing_excluded_at)}` : ""}. No Order Invite texts, campaign texts, or offers added to order confirmations. The order confirmation itself still sends.`
                        : "Order Invite and campaigns can text them when they qualify, and order confirmations can include offers. Order confirmations always send."}
                    </p>
                    <Button variant="outline" className="mt-2 h-11" onClick={() => onToggleMarketing(contact)}>
                      {contact.marketing_excluded ? (
                        <>
                          <MessageSquare className="h-4 w-4" aria-hidden="true" />
                          Allow marketing texts again
                        </>
                      ) : (
                        <>
                          <MessageSquareOff className="h-4 w-4" aria-hidden="true" />
                          Don't send marketing texts
                        </>
                      )}
                    </Button>
                  </section>
                )}

                <section>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">History</h3>
                  {history.isLoading ? (
                    <div className="space-y-4" aria-hidden="true">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex gap-3">
                          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                          <div className="flex-1 space-y-2 pt-1">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : history.isError ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-status-danger/40 bg-status-danger/10 px-3 py-2 text-sm text-status-danger">
                      <span>Couldn't load this history.</span>
                      <Button variant="outline" className="h-11" onClick={() => history.refetch()}>
                        Try again
                      </Button>
                    </div>
                  ) : !history.data || history.data.timeline.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No texts with this customer yet.</p>
                  ) : (
                    <ol>
                      {history.data.timeline.map((entry, index, all) => {
                        const visual = TIMELINE_VISUALS[entry.kind];
                        const isLast = index === all.length - 1;
                        return (
                          <li key={entry.id} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${visual.chip}`}>
                                <visual.Icon className="h-4 w-4" aria-hidden="true" />
                              </span>
                              {!isLast && <span className="mt-1 w-px flex-1 bg-border" aria-hidden="true" />}
                            </div>
                            <div className={`min-w-0 flex-1 pt-1 ${isLast ? "" : "pb-4"}`}>
                              <p
                                className={`break-words text-sm font-medium ${
                                  entry.kind === "opt_out" || entry.kind === "failed"
                                    ? "text-status-danger"
                                    : entry.kind === "opt_in"
                                      ? "text-status-success"
                                      : "text-foreground"
                                }`}
                              >
                                {entry.title}
                              </p>
                              <p className="break-words text-xs text-muted-foreground">
                                {[date(entry.at), entry.detail].filter(Boolean).join(" · ")}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </section>
              </div>
            </>
          );
        })()}
      </SheetContent>
    </Sheet>
  );
}
